import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Alert,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { AppButton, AppIconButton } from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";
import { prepService } from "../../../services/prepService";
import type { PrepJobResponse, PrepJobSummaryResponse } from "../../../types/prep";
import PrepExpansionRunDialog from "../components/PrepExpansionRunDialog";
import PrepGraphThumbnail from "../components/PrepGraphThumbnail";
import { usePrepJobRun } from "../hooks/usePrepJobRun";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";

function approvalColor(status?: string): "default" | "success" | "warning" | "error" {
  switch (status) {
    case "APPROVED":
      return "success";
    case "REJECTED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
}

export default function PrepJobListPage() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const navigate = useNavigate();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const packageLimits = usePackageLimits();
  const [summaries, setSummaries] = useState<PrepJobSummaryResponse[]>([]);
  const [graphs, setGraphs] = useState<Record<string, PrepJobResponse["graph"]>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuJob, setMenuJob] = useState<PrepJobSummaryResponse | null>(null);
  const {
    running,
    runJob,
    expansionDialogOpen,
    expansionEstimate,
    confirmExpansionRun,
    cancelExpansionRun,
  } = usePrepJobRun();

  const load = useCallback(async () => {
    try {
      const list = await prepService.listSummaries();
      setSummaries(Array.isArray(list) ? list : []);
      const fullJobs = await prepService.list();
      const map: Record<string, PrepJobResponse["graph"]> = {};
      for (const job of fullJobs) {
        map[job.id] = job.graph;
      }
      setGraphs(map);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunNow = async (jobId: string) => {
    setError(null);
    try {
      const result = await runJob(jobId, true);
      if (result.started) {
        setMessage(t("prep.design.runStarted"));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleDelete = async (job: PrepJobSummaryResponse) => {
    try {
      await confirmDelete({ itemName: job.name });
      await prepService.remove(job.id);
      setMessage(t("prep.list.deleted"));
      await load();
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("prep.list.title")}
        actions={
          <AppButton
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => navigate("/data-prep/new/design")}
          >
            {t("prep.list.create")}
          </AppButton>
        }
      />
      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxPrepJobs != null}>
          {t("packageLimits.jobs", {
            count: formatLimitValue(packageLimits.maxPrepJobs, t("packageLimits.unlimited")),
          })}
        </PackageLimitHint>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      <PageSectionCard title={t("prep.list.listTitle")}>
        {summaries.length === 0 ? (
          <PageEmptyState
            message={t("prep.list.empty")}
            actionLabel={t("prep.list.create")}
            actionIcon={<AddOutlinedIcon />}
            onAction={() => navigate("/data-prep/new/design")}
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("prep.list.graph")}</TableCell>
                <TableCell>{t("prep.list.name")}</TableCell>
                <TableCell>{t("prep.list.targetIndex")}</TableCell>
                <TableCell>{t("prep.list.approval")}</TableCell>
                <TableCell>{t("prep.list.lastRun")}</TableCell>
                <TableCell align="right">{t("prep.list.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaries.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <PrepGraphThumbnail graph={graphs[job.id]} />
                  </TableCell>
                  <TableCell>{job.name}</TableCell>
                  <TableCell>{job.targetIndex ?? "—"}</TableCell>
                  <TableCell>
                    {job.indexApprovalStatus ? (
                      <Chip size="small" label={job.indexApprovalStatus} color={approvalColor(job.indexApprovalStatus)} />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{job.lastRunStatus ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <AppButton size="small" onClick={() => navigate(`/data-prep/${job.id}/design`)}>
                        {t("prep.list.design")}
                      </AppButton>
                      <AppIconButton
                        size="small"
                        title={t("common.actions")}
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuJob(job);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </AppIconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSectionCard>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuJob)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuJob(null);
        }}
      >
        <MenuItem
          disabled={!menuJob || running || menuJob.indexApprovalStatus === "REJECTED"}
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            void handleRunNow(id);
          }}
        >
          <ListItemIcon>
            <PlayArrowOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.list.runNow")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            navigate(`/data-prep/${id}/runs`);
          }}
        >
          <ListItemIcon>
            <HistoryOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.list.runs")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const id = menuJob.id;
            setMenuAnchor(null);
            setMenuJob(null);
            navigate(`/data-prep/${id}/design`);
          }}
        >
          <ListItemIcon>
            <DesignServicesOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("prep.list.design")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuJob) return;
            const job = menuJob;
            setMenuAnchor(null);
            setMenuJob(null);
            void handleDelete(job);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("common.delete")}</ListItemText>
        </MenuItem>
      </Menu>
      <PrepExpansionRunDialog
        open={expansionDialogOpen}
        estimate={expansionEstimate}
        onCancel={cancelExpansionRun}
        onConfirm={() => confirmExpansionRun(true)}
      />
    </PageListContainer>
  );
}
